import React, {type ReactNode, useMemo} from 'react';
import {alpha, Box, useTheme} from '@mui/material';
import {LineChart, type LineChartProps} from '@mui/x-charts/LineChart';
import {ChartsTooltipContainer} from '@mui/x-charts/ChartsTooltip';
import {chartsAxisHighlightClasses, chartsGridClasses} from '@mui/x-charts';
import {ChartGradient, type ChartGradientPT} from '@src/features';
import type {ChartData} from '@api/types.ts';
import {
    defaultTooltipFormatter,
    formatLocalAxisKey,
    formatTickDay,
    formatTooltipDate,
    formatTooltipTime,
    type TooltipFormatterArgs,
} from './helpers.ts';


type LineSeriesInput = Omit<NonNullable<LineChartProps['series']>[number], 'data'> & {
    data?: ChartData[];
};

type AxisItem = NonNullable<LineChartProps['highlightedAxis']>;

type TooltipContextValue = {
    chartLineColor: string;
    tooltipLabel: string;
};

const LineChartTooltipContext = React.createContext<TooltipContextValue | null>(null);

const CustomLineChartTooltip = () => {
    const context = React.useContext(LineChartTooltipContext);
    const {palette} = useTheme();

    if (!context) {
        return null;
    }

    const {chartLineColor, tooltipLabel} = context;

    return (
        <ChartsTooltipContainer trigger="axis" placement="top">
            {tooltipLabel ? (
                <Box
                    sx={{
                        px: 1.5,
                        py: 0.8,
                        borderRadius: '14px',
                        bgcolor: palette.white_100 ?? '#fff',
                        border: `1px solid ${alpha(chartLineColor, 0.35)}`,
                        boxShadow: '0 8px 24px rgba(16, 24, 40, 0.12)',
                        color: palette.dark,
                        fontSize: 16,
                        lineHeight: 1,
                        fontWeight: 500,
                        whiteSpace: 'nowrap',
                        pointerEvents: 'none',
                    }}
                >
                    {tooltipLabel}
                </Box>
            ) : null}
        </ChartsTooltipContainer>
    );
};

type CustomLineChartProps = {
    FirstNode?: ReactNode;
    LastNode?: ReactNode;
    gradientProps?: ChartGradientPT;
    lineColor?: string;
    data?: ChartData[];
    extraSeries?: LineSeriesInput[];
    skeletonMode?: boolean;
    axisDates?: string[];
    xAxisId?: string;
    syncedAxis?: AxisItem;
    onSyncedAxisChange?: (axisItems: AxisItem) => void;
    tooltipFormatter?: (args: TooltipFormatterArgs) => string;
    seriesValueFormatter?: (value: number | null) => string;
    yAxisValueFormatter?: (value: number) => string;
    yAxisConfig?: LineChartProps['yAxis'];
} & Omit<
    LineChartProps,
    | 'series'
    | 'xAxis'
    | 'yAxis'
    | 'highlightedAxis'
    | 'tooltipAxis'
    | 'onHighlightedAxisChange'
    | 'onTooltipAxisChange'
>;

const buildSeriesData = (source: ChartData[] | undefined, axisDates: string[]) => {
    const valueByDate = new Map(
        (source ?? []).map((item) => [
            formatLocalAxisKey(item.date),
            item.value === 0 ? null : item.value,
        ]),
    );

    return axisDates.map((date) => valueByDate.get(date) ?? null);
};

const AxisHighlightMarkers = ({
                                  markerId,
                                  color,
                              }: {
    markerId: string;
    color: string;
}) => {
    const markerFill = alpha(color, 0.38);

    return (
        <defs>
            <marker
                id={`${markerId}-top`}
                markerWidth="10"
                markerHeight="10"
                refX="5"
                refY="5"
                orient="0"
            >
                <path d="M1 2 L5 8 L9 2 Z" fill={markerFill}/>
            </marker>

            <marker
                id={`${markerId}-bottom`}
                markerWidth="10"
                markerHeight="10"
                refX="5"
                refY="5"
                orient="0"
            >
                <path d="M1 8 L5 2 L9 8 Z" fill={markerFill}/>
            </marker>
        </defs>
    );
};

export const CustomLineChart = (props: CustomLineChartProps) => {
    const {
        gradientProps,
        extraSeries,
        sx,
        FirstNode,
        LastNode,
        lineColor,
        margin,
        skeletonMode = false,
        data,
        axisDates: externalAxisDates,
        xAxisId = 'shared-x-axis',
        syncedAxis,
        onSyncedAxisChange,
        tooltipFormatter,
        seriesValueFormatter,
        yAxisValueFormatter,
        yAxisConfig,
        ...rest
    } = props;

    const {palette} = useTheme();

    const chartLineColor = skeletonMode ? palette.light_dark : lineColor ?? palette.accent;
    const highlightMarkerId = `${gradientProps?.gradientId ?? xAxisId}-axis-highlight`;

    const localAxisDates = useMemo(() => {
        const dates = [
            ...(data ?? []).map((item) => formatLocalAxisKey(item.date)),
            ...(extraSeries ?? []).flatMap((series) =>
                (series.data ?? []).map((item) => formatLocalAxisKey(item.date)),
            ),
        ];

        return Array.from(new Set(dates)).sort((a, b) => a.localeCompare(b));
    }, [data, extraSeries]);

    const axisDates = externalAxisDates ?? localAxisDates;


    const seriesAll = useMemo(() => {
        const main = {
            id: 'main',
            data: buildSeriesData(data, axisDates),
            area: true,
            showMark: false,
            color: chartLineColor,
            valueFormatter: seriesValueFormatter ?? ((value: number | null) => (value == null ? '' : String(value))),
        } satisfies NonNullable<LineChartProps['series']>[number];

        const secondary =
            extraSeries?.map((series) => ({
                ...series,
                data: buildSeriesData(series.data, axisDates),
                valueFormatter:
                    series.valueFormatter ??
                    seriesValueFormatter ??
                    ((value: number | null) => (value == null ? '' : String(value))),
            })) ?? [];

        return [main, ...secondary];
    }, [data, extraSeries, axisDates, chartLineColor, seriesValueFormatter]);

    const [yMin, yMax] = useMemo(() => {
        const allValues = seriesAll.flatMap((series) =>
            (series.data ?? []).filter((value): value is number => typeof value === 'number'),
        );

        if (!allValues.length) {
            return [0, 5];
        }

        const min = Math.min(...allValues);
        const max = Math.max(...allValues);

        const ymin = Math.max(0, Math.floor(min / 5) * 5);
        const ymax = Math.max(5, Math.ceil(max / 5) * 5);

        return [ymin, ymax];
    }, [seriesAll]);

    const activeAxisItem = useMemo(() => {
        if (!syncedAxis?.length) {
            return undefined;
        }

        return syncedAxis.find((item) => String(item.axisId) === String(xAxisId)) ?? syncedAxis[0];
    }, [syncedAxis, xAxisId]);

    const activeAxisValue = activeAxisItem ? axisDates[activeAxisItem.dataIndex] : undefined;

    const tooltipLabel = useMemo(() => {
        if (!activeAxisItem) {
            return '';
        }

        const dataIndex = activeAxisItem.dataIndex;

        if (!activeAxisValue) {
            return '';
        }

        const value = (seriesAll[0]?.data?.[dataIndex] ?? null) as number | null;
        const timeLabel = formatTooltipTime(activeAxisValue);
        const resolvedTooltipFormatter = tooltipFormatter ?? defaultTooltipFormatter;

        return resolvedTooltipFormatter({
            value,
            axisValue: activeAxisValue,
            timeLabel,
            dataIndex,
        });
    }, [activeAxisItem, activeAxisValue, seriesAll, tooltipFormatter]);

    const tooltipContextValue = useMemo(
        () => ({
            chartLineColor,
            tooltipLabel,
        }),
        [chartLineColor, tooltipLabel],
    );

    const defaultYAxis = useMemo<NonNullable<LineChartProps['yAxis']>>(
        () => [
            {
                min: yMin,
                max: yMax,
                tickMinStep: 5,
                tickNumber: 4,
                valueFormatter: yAxisValueFormatter,
            },
        ],
        [yAxisValueFormatter, yMin, yMax],
    );

    const settings: LineChartProps = useMemo(
        () => ({
            series: seriesAll,
            hideLegend: true,
            skipAnimation: true,

            xAxis: [
                {
                    id: xAxisId,
                    scaleType: 'point',
                    data: axisDates,
                    valueFormatter: (value, context) => {
                        const date = String(value);

                        if (context.location === 'tooltip') {
                            return formatTooltipDate(date);
                        }

                        return formatTickDay(date);
                    },
                },
            ],

            yAxis: yAxisConfig ?? defaultYAxis,

            margin: {
                top: 20,
                right: 10,
                bottom: 0,
                left: 0,
                ...(typeof margin === 'object' && margin),
            },

            grid: {
                horizontal: true,
            },

            disableAxisListener: skeletonMode,
            axisHighlight: skeletonMode ? {x: 'none', y: 'none'} : {x: 'line', y: 'none'},

            highlightedAxis: skeletonMode ? undefined : syncedAxis,
            tooltipAxis: skeletonMode ? undefined : syncedAxis,

            onHighlightedAxisChange: skeletonMode ? undefined : onSyncedAxisChange,
            onTooltipAxisChange: skeletonMode ? undefined : onSyncedAxisChange,

            slots: skeletonMode
                ? undefined
                : {
                    tooltip: CustomLineChartTooltip,
                },

            slotProps: {
                tooltip: {
                    trigger: skeletonMode ? 'none' : 'axis',
                },
            },

            sx: {
                '& .MuiAreaElement-root[data-series="main"]': {
                    fill: gradientProps?.gradientId ? `url('#${gradientProps.gradientId}')` : undefined,
                    opacity: skeletonMode ? 0.18 : 1,
                },

                '& .MuiLineElement-root[data-series="main"]': {
                    stroke: chartLineColor,
                    strokeWidth: 1,
                },

                [`& .${chartsGridClasses.line}`]: {
                    strokeDasharray: '2 2',
                },

                [`& .${chartsGridClasses.horizontalLine}`]: {
                    stroke: palette.dark,
                    strokeOpacity: 0.15,
                },

                [`& .${chartsAxisHighlightClasses.root}`]: {
                    stroke: alpha(chartLineColor, 0.28),
                    strokeDasharray: 'none',
                    strokeWidth: 1,
                    markerStart: `url(#${highlightMarkerId}-top)`,
                    markerEnd: `url(#${highlightMarkerId}-bottom)`,
                },

                '& .MuiChartsAxis-left .MuiChartsAxis-tickLabel': {
                    fill: skeletonMode ? 'transparent' : palette.light_dark,
                },

                '& .MuiChartsAxis-bottom .MuiChartsAxis-tickLabel': {
                    fill: palette.light_dark,
                },

                '& .MuiChartsAxis-line': {
                    stroke: 'transparent !important',
                },

                '& .MuiChartsAxis-tick': {
                    stroke: 'transparent !important',
                },

                ...sx,
            },
        }),
        [
            defaultYAxis,
            yAxisConfig,
            axisDates,
            chartLineColor,
            gradientProps?.gradientId,
            highlightMarkerId,
            margin,
            onSyncedAxisChange,
            palette,
            seriesAll,
            skeletonMode,
            sx,
            syncedAxis,
            xAxisId,
            yMax,
            yMin,
        ],
    );

    return (
        <Box
            sx={{
                minWidth: 0,
                flexGrow: 1,
                height: '100%',
                bgcolor: 'white',
                position: 'relative',
                borderRadius: '8px',
                overflow: 'hidden',
            }}
        >
            {FirstNode}

            <LineChartTooltipContext.Provider value={tooltipContextValue}>
                <LineChart {...settings} {...rest}>
                    <ChartGradient
                        gradientId={gradientProps?.gradientId}
                        firstStopColor={gradientProps?.firstStopColor}
                        firstStopOpacity={gradientProps?.firstStopOpacity}
                        secondStopColor={gradientProps?.secondStopColor}
                        secondStopOpacity={gradientProps?.secondStopOpacity}
                    />

                    <AxisHighlightMarkers markerId={highlightMarkerId} color={chartLineColor}/>
                </LineChart>
            </LineChartTooltipContext.Provider>

            {LastNode}
        </Box>
    );
};
